import React from 'react'
import FormSectionTitle from '../../components/FormSectionTitle'
import Typography from '../../components/Typography'
import Button from '../../components/Button'
import type { DropdownSelectOption } from '../../components/DropdownSelect'
import DropdownSelect from '../../components/DropdownSelect'
import IconButton from '../../components/IconButton'
import { capitalize, makeStyles } from '@material-ui/core'
import moment from 'moment'
import AddIcon from '@material-ui/icons/Add'
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle'
import { useFormikContext } from 'formik'
import type { ProfileFormikValues } from '../ProfileFormik'
import { useIsMobile } from '../../hooks'
import Switch from '../../components/Switch'
import Grid from '../../components/Grid'
import Collapse from '../../components/Collapse'

interface TimeRange {
  start?: string
  end?: string
}

interface WeeklyAvailability {
  [day: string]: TimeRange[]
}

const useStyles = makeStyles((theme) => ({
  content: {
    overflowX: 'hidden'
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 3fr 7fr 1fr',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2.5),

    alignItems: 'flex-start',
    '& > :nth-child(2)': {
      marginTop: '9px'
    },
    '& > :last-child': {
      marginTop: '6px',
      [theme.breakpoints.down('sm')]: {
        marginTop: '10px'
      }
    },
    [theme.breakpoints.up('xl')]: {
      marginRight: theme.spacing(-1)
    }
  },
  rowText: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 500,
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }
  },
  timeRangeContainer: {
    '& > *': {
      height: '39px',
      display: 'flex',
      alignItems: 'center'
    },
    '& > *:not(:first-child)': {
      marginTop: theme.spacing(2.5)
    }
  },
  timeRange: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(1, 0, 1, 1)
    }
  },
  dropdown: {
    height: theme.typography.pxToRem(36)
  },
  dropdownInput: {
    padding: theme.spacing(0),
    paddingLeft: theme.spacing(0.5)
  },
  addButtonContainer: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'flex-end',
      '& > button': {
        padding: 0
      }
    }
  },
  disabled: {
    opacity: 0.5
  },
  removeButton: {
    padding: theme.spacing(0),
    marginLeft: theme.spacing(1),
    '&:hover': {
      color: theme.palette.error.main
    }
  }
}))

const timeOptions: DropdownSelectOption[] = Array.from({ length: 35 }, (_, i) => {
  const time = moment()
    .startOf('day')
    .add(5 * 60 + i * 30, 'minutes')
  return {
    value: time.format('hh:mma'),
    label: time.format('h:mma')
  }
})

// --- Helper functions ---
const getFilteredStartOptions = (prevEnd: string | null) =>
  timeOptions.filter((option) => !prevEnd || moment(option.value, 'hh:mma').isSameOrAfter(moment(prevEnd, 'hh:mma')))

const getFilteredEndOptions = (start: string | undefined, nextStart: string | null) =>
  timeOptions.filter((option) => {
    const afterStart = moment(option.value, 'hh:mma').isAfter(moment(start, 'hh:mma'))
    if (!nextStart) return afterStart
    const beforeNext = moment(option.value, 'hh:mma').isSameOrBefore(moment(nextStart, 'hh:mma'))
    return afterStart && beforeNext
  })

function TimeRangeRow({
  timeRange,
  index,
  day,
  prevEnd,
  nextStart,
  onChange,
  onRemove,
  classes
}: {
  timeRange: TimeRange
  index: number
  day: keyof WeeklyAvailability
  prevEnd: string | null
  nextStart: string | null
  onChange: (option: DropdownSelectOption, index: number, type: 'start' | 'end', day: keyof WeeklyAvailability) => void
  onRemove: (day: keyof WeeklyAvailability, index: number) => void
  classes: Record<string, string>
}) {
  return (
    <div className={classes.timeRange}>
      <DropdownSelect
        className={classes.dropdown}
        inputProps={{ className: classes.dropdownInput }}
        hideIcon
        variant="single"
        value={timeOptions.find((option) => option.value === timeRange.start)}
        options={getFilteredStartOptions(prevEnd)}
        onChange={(option: DropdownSelectOption) => onChange(option, index, 'start', day)}
        denseOptions
      />
      <Typography>—</Typography>
      <DropdownSelect
        className={classes.dropdown}
        inputProps={{ className: classes.dropdownInput }}
        hideIcon
        variant="single"
        value={timeOptions.find((option) => option.value === timeRange.end)}
        options={getFilteredEndOptions(timeRange.start, nextStart)}
        onChange={(option: DropdownSelectOption) => onChange(option, index, 'end', day)}
        denseOptions
      />
      <IconButton className={classes.removeButton} size="small" onClick={() => onRemove(day, index)}>
        <RemoveCircleIcon fontSize="small" />
      </IconButton>
    </div>
  )
}

export default function WeeklyHours() {
  const classes = useStyles()
  const mobile = useIsMobile()
  const { values, setFieldValue } = useFormikContext<ProfileFormikValues>()
  const isXS = useIsMobile({ xs: true })

  const availability: WeeklyAvailability = {
    sunday: values.preferences.availability?.sunday || [],
    monday: values.preferences.availability?.monday || [],
    tuesday: values.preferences.availability?.tuesday || [],
    wednesday: values.preferences.availability?.wednesday || [],
    thursday: values.preferences.availability?.thursday || [],
    friday: values.preferences.availability?.friday || [],
    saturday: values.preferences.availability?.saturday || []
  }

  const handleChange = (
    option: DropdownSelectOption,
    index: number,
    type: 'start' | 'end',
    day: keyof WeeklyAvailability
  ) => {
    setFieldValue(`preferences.availability.${day}`, [
      ...availability[day].slice(0, index),
      {
        ...availability[day][index],
        [type]: option.value
      },
      ...availability[day].slice(index + 1)
    ])
  }

  const handleAddTimeRange = (day: keyof WeeklyAvailability) => {
    const prevRanges = availability[day]
    let start = '09:00am'
    if (prevRanges.length > 0) {
      const prevEnd = prevRanges[prevRanges.length - 1]?.end
      if (prevEnd) {
        start = prevEnd
      }
    }
    setFieldValue(`preferences.availability.${day}`, [
      ...prevRanges,
      {
        start,
        end: '05:00pm'
      }
    ])
  }

  const handleRemoveTimeRange = (day: keyof WeeklyAvailability, index: number) => {
    setFieldValue(`preferences.availability.${day}`, [
      ...availability[day].slice(0, index),
      ...availability[day].slice(index + 1)
    ])
  }

  const isFullDayCovered = (day: keyof WeeklyAvailability): boolean => {
    const ranges = availability[day]
    if (!ranges.length) return false
    const lastRange = ranges[ranges.length - 1]
    return lastRange?.end === timeOptions[timeOptions.length - 1].value
  }

  return (
    <>
      <FormSectionTitle
        title={
          <Typography variant="h3" weight={500}>
            Default Schedule
          </Typography>
        }
        subTitle={'Adjust your weekly availability for events.'}
      />
      <div className={classes.content}>
        {Object.keys(availability).map((day) =>
          isXS ? (
            <Grid container key={day} alignItems="center" style={{ marginTop: 8, marginBottom: 8 }}>
              <Grid
                item
                xs={6}
                container
                alignItems="center"
                className={!availability[day].length ? classes.disabled : undefined}
              >
                <Grid item>
                  <Switch
                    checked={!!availability[day].length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleAddTimeRange(day as keyof WeeklyAvailability)
                      } else {
                        setFieldValue(`preferences.availability.${day}`, [])
                      }
                    }}
                    color="primary"
                  />
                </Grid>
                <Grid item>
                  <Typography variant="h4" className={classes.rowText} color="textPrimary">
                    {capitalize(day)}
                  </Typography>
                </Grid>
              </Grid>
              {availability[day].length && !isFullDayCovered(day as keyof WeeklyAvailability) ? (
                <Grid item xs={6} container justifyContent="flex-end">
                  <Button
                    size={mobile ? 'medium' : 'small'}
                    variant="hoverUnderline"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddTimeRange(day)}
                  >
                    Add
                  </Button>
                </Grid>
              ) : (
                <Grid item xs={6}>
                  <Typography variant="body2" className={classes.rowText} color="textSecondary">
                    <i>Unavailable</i>
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Collapse show={!!availability[day].length}>
                  <div className={classes.timeRangeContainer}>
                    {availability[day].map((timeRange, index) => (
                      <TimeRangeRow
                        key={index}
                        timeRange={timeRange}
                        index={index}
                        day={day as keyof WeeklyAvailability}
                        prevEnd={index > 0 ? availability[day][index - 1]?.end : null}
                        nextStart={index < availability[day].length - 1 ? availability[day][index + 1]?.start : null}
                        onChange={handleChange}
                        onRemove={handleRemoveTimeRange}
                        classes={classes}
                      />
                    ))}
                  </div>
                </Collapse>
              </Grid>
            </Grid>
          ) : (
            <div key={day} className={classes.row}>
              <div>
                <Switch
                  checked={!!availability[day].length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleAddTimeRange(day as keyof WeeklyAvailability)
                    } else {
                      setFieldValue(`preferences.availability.${day}`, [])
                    }
                  }}
                  color="primary"
                />
              </div>
              <Typography
                variant="h4"
                className={classes.rowText}
                color={availability[day].length ? 'textPrimary' : 'textSecondary'}
              >
                {capitalize(day)}
              </Typography>
              <div className={classes.timeRangeContainer}>
                {availability[day].length > 0 ? (
                  availability[day].map((timeRange, index) => (
                    <TimeRangeRow
                      key={index}
                      timeRange={timeRange}
                      index={index}
                      day={day as keyof WeeklyAvailability}
                      prevEnd={index > 0 ? availability[day][index - 1]?.end : null}
                      nextStart={index < availability[day].length - 1 ? availability[day][index + 1]?.start : null}
                      onChange={handleChange}
                      onRemove={handleRemoveTimeRange}
                      classes={classes}
                    />
                  ))
                ) : (
                  <Typography variant="body2" className={classes.rowText} color="textSecondary">
                    <i>Unavailable</i>
                  </Typography>
                )}
              </div>
              <div className={classes.addButtonContainer}>
                <Button
                  size={mobile ? 'medium' : 'small'}
                  style={{}}
                  variant="hoverUnderline"
                  color="primary"
                  startIcon={!isFullDayCovered(day as keyof WeeklyAvailability) && <AddIcon />}
                  onClick={() => handleAddTimeRange(day)}
                >
                  {!isFullDayCovered(day as keyof WeeklyAvailability) && 'Add'}
                </Button>
              </div>
            </div>
          )
        )}
      </div>
    </>
  )
}
